import { useEffect, useRef, useState, type RefObject } from 'react';

import { readLastVisitedRow } from '@/lib/findingFilterStorage';

/** Which visible row we should return to: the row last opened from this list,
 *  but only when it's still in the current (filtered) set — filters/sort may
 *  have changed while the detail page was open, in which case there's nothing
 *  to return to. */
export function pickReturnRowId(
  lastVisitedId: string | null,
  visibleIds: readonly string[],
): string | null {
  if (!lastVisitedId) return null;
  return visibleIds.includes(lastVisitedId) ? lastVisitedId : null;
}

/**
 * On return from a detail page, scroll the row the practitioner came from
 * back into view (restoring roughly where they were in a long list) and
 * expose its id so the row can render a highlight. Shared by the findings
 * list, audit log, and suppliers tab.
 *
 * Reads the last-visited id once per mount (navigating away unmounts the
 * table, so it only changes across mounts) and scrolls exactly once, when the
 * target row first exists in the DOM.
 *
 * `storageKey` is the session-storage key the table writes its last-visited
 * row under (e.g. `lastVisitedFindingKey(runId)` /
 * `lastVisitedSupplierKey(runId)`). Attach `containerRef` to the scroll
 * container and stamp each row with `data-row-id={rowId}`.
 */
export function useReturnToRow(
  storageKey: string,
  visibleIds: readonly string[],
): {
  lastVisitedId: string | null;
  containerRef: RefObject<HTMLDivElement | null>;
} {
  const [lastVisitedId] = useState<string | null>(() => readLastVisitedRow(storageKey));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrolled = useRef(false);
  const target = pickReturnRowId(lastVisitedId, visibleIds);

  useEffect(() => {
    if (scrolled.current || target === null) return;
    // Match on the attribute value rather than building a selector, so a
    // row id with quotes/brackets (e.g. a supplier name) can't break it.
    const rows = containerRef.current?.querySelectorAll<HTMLElement>('[data-row-id]');
    const row = rows && Array.from(rows).find((el) => el.dataset.rowId === target);
    if (row) {
      row.scrollIntoView({ block: 'center' });
      scrolled.current = true;
    }
  }, [target]);

  return { lastVisitedId, containerRef };
}
